import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import SEO from '../components/SEO';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Breadcrumb from '../components/Breadcrumb';
import InternalLinks from '../components/InternalLinks';
import { 
  CheckCircle, 
  ArrowRight, 
  Brain,
  Target,
  Clock,
  Shield,
  FileText,
  AlertTriangle,
  BarChart3,
  Sparkles,
  Upload
} from 'lucide-react';

export default function AnalyseDcePage() {
  return (
    <>
      <SEO 
        title="Analyse DCE | Comprendre et Analyser un Dossier de Consultation"
        description="Comment analyser un DCE rapidement ? Structure, RC, CCTP, critères d'attribution. Synthèse IA en 30 secondes. Guide complet pour comprendre les DCE."
        path="/analyse-dce"
      />
      <div className="min-h-screen bg-white">
        <Header />

        {/* Hero Section */}
        <section className="bg-gradient-to-br from-violet-900 via-indigo-900 to-slate-900 text-white py-20">
          <div className="container mx-auto px-4">
            <Breadcrumb items={[{ label: "Analyse DCE", href: "/analyse-dce" }]} />
            <div className="text-center">
              <div className="inline-flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full text-sm mb-6">
                <Brain className="w-4 h-4" />
                <span>Powered by GPT-5.2</span>
              </div>
              <h1 className="text-4xl md:text-6xl font-bold mb-6">
                Analyse de DCE
                <br />
                <span className="text-violet-300">par Intelligence Artificielle</span>
              </h1>
              <p className="text-xl text-slate-300 max-w-3xl mx-auto mb-8">
                Uploadez votre dossier de consultation, notre IA l'analyse en 30 secondes et vous livre une synthèse complète : critères d'attribution, délais, pénalités, points de vigilance.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/analyse-gratuite">
                  <Button size="lg" className="bg-white text-violet-900 hover:bg-slate-100 rounded-full px-8">
                    Analyser un DCE gratuitement
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Qu'est-ce qu'un DCE */}
        <section className="py-16 bg-slate-50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-slate-900 mb-6">
                Qu'est-ce qu'un DCE (Dossier de Consultation des Entreprises) ?
              </h2>
              <div className="prose prose-lg max-w-none text-slate-600">
                <p>
                  Le <strong>DCE</strong>, ou Dossier de Consultation des Entreprises, est l'ensemble des documents fournis par l'acheteur public pour décrire son besoin et les modalités de réponse à l'appel d'offres. C'est la "bible" de chaque consultation.
                </p>
                <p>
                  Un DCE complet comprend généralement :
                </p>
                <ul>
                  <li><strong>Le Règlement de Consultation (RC)</strong> : les règles du jeu (critères d'attribution, documents à fournir, délais)</li>
                  <li><strong>L'Acte d'Engagement (AE)</strong> : le document contractuel à signer</li>
                  <li><strong>Le Cahier des Clauses Administratives Particulières (CCAP)</strong> : les conditions contractuelles (paiement, pénalités, résiliation)</li>
                  <li><strong>Le Cahier des Clauses Techniques Particulières (CCTP)</strong> : la description détaillée du besoin technique</li>
                  <li><strong>Le Bordereau des Prix (BPU/DQE)</strong> : la structure de l'offre financière</li>
                </ul>
                <p>
                  Analyser correctement un DCE est <strong>crucial</strong> pour préparer une offre pertinente. Mais cette analyse prend du temps : en moyenne <strong>2 à 4 heures</strong> par dossier pour un professionnel expérimenté.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Le problème */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-slate-900 mb-12 text-center">
              Le défi de l'analyse des DCE
            </h2>
            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {[
                {
                  icon: Clock,
                  title: "Temps considérable",
                  desc: "Lire et analyser un DCE complet demande plusieurs heures. Multipliez par le nombre de consultations suivies et le temps explose."
                },
                {
                  icon: FileText,
                  title: "Volume de documents",
                  desc: "Un DCE peut contenir des dizaines de documents et des centaines de pages. Les informations clés sont dispersées."
                },
                {
                  icon: AlertTriangle,
                  title: "Risque d'oubli",
                  desc: "Une clause cachée au milieu du CCAP, une exigence technique noyée dans les annexes... Passer à côté peut coûter cher."
                },
                {
                  icon: Target,
                  title: "Complexité juridique",
                  desc: "Le vocabulaire juridique des marchés publics n'est pas toujours accessible. Mal interpréter une clause peut être fatal."
                }
              ].map((problem, i) => (
                <Card key={`item-${i}`} className="border-slate-200">
                  <CardContent className="p-6">
                    <div className="flex gap-4">
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                          <problem.icon className="w-6 h-6 text-red-600" />
                        </div>
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-slate-900 mb-2">{problem.title}</h3>
                        <p className="text-slate-600">{problem.desc}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* La solution IA */}
        <section className="py-16 bg-violet-900 text-white">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold mb-12 text-center">
              Comment l'IA révolutionne l'analyse des DCE
            </h2>
            <div className="max-w-4xl mx-auto">
              <div className="prose prose-lg prose-invert max-w-none mb-8">
                <p>
                  L'<strong>intelligence artificielle</strong> change la donne. En quelques secondes, notre IA analyse l'intégralité de votre DCE et en extrait les informations essentielles de manière structurée.
                </p>
              </div>
              <div className="grid md:grid-cols-3 gap-8">
                {[
                  {
                    step: "1",
                    title: "Upload",
                    desc: "Téléchargez votre DCE au format PDF ou ZIP. Notre système accepte les dossiers multi-documents."
                  },
                  {
                    step: "2",
                    title: "Analyse IA",
                    desc: "GPT-5.2 lit et analyse chaque document, identifie les clauses clés et structure les informations."
                  },
                  {
                    step: "3",
                    title: "Synthèse",
                    desc: "Recevez une fiche de synthèse complète : critères, délais, montants, points de vigilance."
                  }
                ].map((step, i) => (
                  <div key={`item-${i}`} className="text-center">
                    <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                      {step.step}
                    </div>
                    <h3 className="text-xl font-semibold mb-2">{step.title}</h3>
                    <p className="text-slate-300">{step.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Ce que l'IA extrait */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-slate-900 mb-12 text-center">
              Les informations extraites par notre IA
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {[
                {
                  icon: BarChart3,
                  title: "Critères d'attribution",
                  items: ["Pondération prix/qualité", "Sous-critères techniques", "Grille de notation"]
                },
                {
                  icon: Clock,
                  title: "Délais clés",
                  items: ["Date limite de dépôt", "Délai d'exécution", "Planning prévisionnel"]
                },
                {
                  icon: AlertTriangle,
                  title: "Pénalités et risques",
                  items: ["Pénalités de retard", "Clauses résolutoires", "Garanties exigées"]
                },
                {
                  icon: FileText,
                  title: "Documents requis",
                  items: ["Pièces de candidature", "Pièces de l'offre", "Formats attendus"]
                },
                {
                  icon: Target,
                  title: "Exigences techniques",
                  items: ["Spécifications obligatoires", "Normes à respecter", "Certifications requises"]
                },
                {
                  icon: Shield,
                  title: "Conditions contractuelles",
                  items: ["Modalités de paiement", "Révision des prix", "Durée du marché"]
                }
              ].map((category, i) => (
                <Card key={`item-${i}`} className="border-slate-200 hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 bg-violet-100 rounded-xl flex items-center justify-center">
                        <category.icon className="w-5 h-5 text-violet-600" />
                      </div>
                      <h3 className="text-lg font-semibold text-slate-900">{category.title}</h3>
                    </div>
                    <ul className="space-y-2">
                      {category.items.map((item, j) => (
                        <li key={j} className="flex items-center gap-2 text-slate-600">
                          <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Avantages */}
        <section className="py-16 bg-slate-50">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-slate-900 mb-12 text-center">
              Les avantages de l'analyse IA des DCE
            </h2>
            <div className="max-w-4xl mx-auto">
              <div className="grid md:grid-cols-2 gap-8">
                {[
                  {
                    title: "Gain de temps massif",
                    desc: "Ce qui prenait 2 à 4 heures se fait désormais en 30 secondes. Analysez 10 DCE dans le temps qu'il fallait pour en lire un seul.",
                    metric: "30 sec"
                  },
                  {
                    title: "Aucun oubli",
                    desc: "L'IA parcourt chaque page, chaque annexe. Les clauses cachées sont détectées et mises en évidence.",
                    metric: "100%"
                  },
                  {
                    title: "Décisions éclairées",
                    desc: "Avec une vue d'ensemble claire, décidez rapidement si le marché vaut la peine d'y consacrer du temps.",
                    metric: "Go/No Go"
                  },
                  {
                    title: "Format exploitable",
                    desc: "La synthèse est structurée et exportable. Partagez-la facilement avec vos équipes ou vos partenaires.",
                    metric: "PDF/JSON"
                  }
                ].map((avantage, i) => (
                  <Card key={`item-${i}`} className="border-violet-200 bg-white">
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <h3 className="text-xl font-semibold text-slate-900">{avantage.title}</h3>
                        <span className="px-3 py-1 bg-violet-100 text-violet-700 rounded-full text-sm font-medium">
                          {avantage.metric}
                        </span>
                      </div>
                      <p className="text-slate-600">{avantage.desc}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Cas d'usage */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-slate-900 mb-6 text-center">
              Qui utilise l'analyse IA des DCE ?
            </h2>
            <div className="max-w-4xl mx-auto prose prose-lg text-slate-600">
              <p>
                L'analyse automatisée des DCE répond aux besoins de nombreux professionnels :
              </p>
              <ul>
                <li><strong>Dirigeants de PME</strong> : évaluez rapidement la pertinence d'un marché avant de mobiliser vos équipes</li>
                <li><strong>Responsables commerciaux</strong> : traitez plus de consultations sans augmenter vos effectifs</li>
                <li><strong>Consultants</strong> : analysez les DCE de vos clients en quelques minutes</li>
                <li><strong>Services achats</strong> : vérifiez la conformité des DCE émis par vos prescripteurs</li>
                <li><strong>Juristes</strong> : identifiez instantanément les clauses sensibles à analyser en détail</li>
              </ul>
              <p>
                Quelle que soit votre activité, si vous répondez régulièrement aux appels d'offres, l'analyse IA vous fera <strong>gagner des heures chaque semaine</strong>.
              </p>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-gradient-to-r from-violet-600 to-indigo-600 text-white">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold mb-4">
              Testez l'analyse IA de DCE gratuitement
            </h2>
            <p className="text-xl text-violet-100 mb-8 max-w-2xl mx-auto">
              Uploadez votre premier DCE et découvrez la puissance de l'analyse automatisée. Sans inscription pour votre premier essai.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/analyse-gratuite">
                <Button size="lg" className="bg-white text-violet-900 hover:bg-slate-100 rounded-full px-8">
                  <Upload className="mr-2 w-5 h-5" />
                  Analyser un DCE maintenant
                </Button>
              </Link>
              <Link to="/analyse-dce">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 rounded-full px-8">
                  En savoir plus
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Internal Links */}
        <InternalLinks currentPage="analyse" />

        <Footer />
      </div>
    </>
  );
}
